/* MolCellQuest — 表示フォーマッタ
   ──────────────────────────────────────────────────────────
   進化ポイント(evo)は内部スカラー、地質年代(Ma = 百万年前)は表示用。
   両者の変換は data/journeyEngine.js が担当する。
   ────────────────────────────────────────────────────────── */

/** 小数を最大1桁に丸め、末尾の .0 を落とす（12.0 → "12", 3.45 → "3.5"） */
export function trimNum(n) {
  if (!isFinite(n)) return "∞";
  const s = n >= 100 ? String(Math.round(n)) : n.toFixed(1);
  return s.replace(/\.0$/, "");
}

/** カンマ区切り整数 */
export function fmtInt(n) {
  return Math.round(n || 0).toLocaleString("en-US");
}

/**
 * 地質年代（Ma = 百万年前）を日本語表記に。
 * レンジが46億年〜1万年と6桁またぐので、桁に応じて単位を切り替える。
 *   4600   → "46 億年前"
 *   538.8  → "5.4 億年前"
 *   66     → "6600 万年前"
 *   2.58   → "258 万年前"
 *   0.0117 → "11,700 年前"
 *   0      → "現在"
 */
export function fmtEra(ma) {
  if (!isFinite(ma)) return "—";
  if (ma <= 0) return "現在";
  if (ma >= 100) return trimNum(ma / 100) + " 億年前";
  if (ma >= 1) return fmtInt(ma * 100) + " 万年前";
  if (ma >= 0.001) return fmtInt(ma * 1e6) + " 年前";
  return "ほぼ現在";
}

/** 未来ステージ用。ma は「現在から何百万年後か」の正の値。 */
export function fmtFuture(ma) {
  if (ma <= 0) return "現在";
  if (ma >= 100) return "+" + trimNum(ma / 100) + " 億年後";
  if (ma >= 1) return "+" + fmtInt(ma * 100) + " 万年後";
  return "+" + fmtInt(Math.max(1, ma * 1e6)) + " 年後";
}

/** 進化ポイントそのものの表示（統計・ランキング用の補助表示） */
export function fmtEvo(v) {
  v = Math.max(0, v || 0);
  // 旅の序盤は 0.05 のような小さな値になる。丸めて "0" にしてしまうと
  // 「+0 獲得」と表示されてしまうので、小さい値ほど桁を残す。
  if (v === 0) return "0";
  if (v < 0.01) return v.toFixed(3);
  if (v < 1) return v.toFixed(2);
  if (v < 10) return trimNum(v);
  if (v < 1e4) return fmtInt(v);
  if (v < 1e8) return trimNum(v / 1e4) + " 万";
  if (v < 1e12) return trimNum(v / 1e8) + " 億";
  return trimNum(v / 1e12) + " 兆";
}

/** 1問あたりの解答時間 */
export function fmtSolveTime(ms) {
  if (ms == null || !isFinite(ms)) return "—";
  const sec = ms / 1000;
  if (sec < 60) return (sec < 10 ? sec.toFixed(1) : String(Math.round(sec))) + "秒";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}分${s}秒`;
}

/** 累計学習時間 */
export function fmtDuration(ms) {
  const min = Math.floor((ms || 0) / 60000);
  if (min < 60) return `${min}分`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}時間` : `${h}時間${m}分`;
}

/** "YYYY-MM-DD"（ローカルタイム） */
export function today(d) {
  const t = d || new Date();
  return (
    t.getFullYear() +
    "-" +
    String(t.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(t.getDate()).padStart(2, "0")
  );
}

/** "YYYY-MM-DD" → "M/D" */
export function shortDate(ymd) {
  const p = String(ymd || "").split("-");
  return p.length === 3 ? `${Number(p[1])}/${Number(p[2])}` : ymd;
}

/** epoch ms → "M/D HH:MM" */
export function fmtStamp(t) {
  const d = new Date(t);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes()
  ).padStart(2, "0")}`;
}

/**
 * Firestore の Timestamp / Date / epoch ms を、人が読む相対表記にする。
 * ランキングの「最終ログイン」用。直近は相対、古いものは日付で出す。
 */
export function fmtSince(v) {
  const ms = toEpochMs(v);
  if (!ms) return "";
  const diff = Date.now() - ms;
  if (diff < 0) return "たった今";
  const min = Math.floor(diff / 60000);
  if (min < 1) return "たった今";
  if (min < 60) return `${min}分前`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour}時間前`;
  const day = Math.floor(hour / 24);
  if (day === 1) return "昨日";
  if (day < 7) return `${day}日前`;
  const d = new Date(ms);
  const y = d.getFullYear() === new Date().getFullYear() ? "" : `${d.getFullYear()}/`;
  return `${y}${d.getMonth() + 1}/${d.getDate()}`;
}

/** Firestore Timestamp（toDate か seconds を持つ）・Date・数値を epoch ms にそろえる */
export function toEpochMs(v) {
  if (!v) return 0;
  if (typeof v === "number") return v;
  if (v instanceof Date) return v.getTime();
  if (typeof v.toDate === "function") {
    try {
      return v.toDate().getTime();
    } catch (e) {
      return 0;
    }
  }
  if (typeof v.seconds === "number") return v.seconds * 1000;
  return 0;
}

/** CSS カスタムプロパティの実値を読む（Canvas 描画で使う） */
export function varColor(name, fallback) {
  if (typeof document === "undefined") return fallback || "#fff";
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback || "#fff";
}

/** HTML エスケープ（ユーザー入力をマークアップに混ぜるとき用） */
export function esc(s) {
  return String(s == null ? "" : s).replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]
  );
}
